import {
    AnalyticsContext,
    initialAnalyticsContext,
} from "@/Contexts/AnalyticsContext";
import { CookieManagerContext } from "@/Contexts/CookieManagerContext";
import { Client, Coach, PageProps } from "@/types/page";
import { User } from "@/types/user";
import { cookieDomainAttribute, getCookie } from "@/utils/CookieJar";
import { usePageProps } from "@/utils/dataLayer";
import { usePage } from "@/utils/turboSafeInertia";
import { loadThm } from "@/utils/utmParams";
import * as amplitude from "@amplitude/analytics-browser";
import { Experiment } from "@amplitude/experiment-js-client";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import {
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";

/**
 * Cookie used by automated tests (Playwright / Appium) to default every
 * unhandled experiment to a specific variant (typically "control").
 */
const E2E_FORCE_DEFAULT_VARIANT_COOKIE = "e2e_force_default_variant";

export function AnalyticsProvider({ children }: PropsWithChildren) {
    const { isImpersonating, impersonatorId } = usePage<PageProps>().props;
    const cookieSettings = useContext(CookieManagerContext).cookieSettings;
    const experimentation = Experiment.initializeWithAmplitudeAnalytics(
        import.meta.env.VITE_AMPLITUDE_API_KEY,
        {
            automaticExposureTracking: false,
        },
    );
    const lastIdentifiedThm = useRef<string | null>(null);
    const lastAmplitudeIdentity = useRef<string | null>(null);
    /**
     * Tracked separately from experimentationInitialized, which the denied path
     * also sets — so it is true while amplitude.init() has never run, and stays
     * true across the consent transition.
     */
    const [amplitudeInitialized, setAmplitudeInitialized] = useState(false);
    const [experimentationInitialized, setExperimentationInitialized] =
        useState(false);

    /**
     * Block Amplitude from initializing until we handle a potential
     * Jeff page redirect. Without this, Amplitude tries to capture
     * initial UTM params from the Jeff short link, which doesn't have
     * them until it redirects.
     */
    const [
        isAmplitudeInitializationBlocked,
        setIsAmplitudeInitializationBlocked,
    ] = useState(window.isTurboPage && route().current() === "dynamic-page");

    const optOut = () => {
        // remove amplitude cookies (start with AMP_), covering every domain
        // scope they may have been set on over time
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            if (cookies[i].trim().indexOf("AMP_") === 0) {
                const cookieName = cookies[i].trim().split("=")[0];
                const expiration =
                    "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
                document.cookie = cookieName + expiration;
                document.cookie =
                    cookieName +
                    expiration +
                    "; domain=." +
                    window.location.hostname;
                document.cookie =
                    cookieName + expiration + cookieDomainAttribute();
            }
        }

        amplitude.setOptOut(true);
    };

    const trackEvent = (
        eventName: string,
        eventProperties?: Record<
            string,
            string | number | boolean | object | null
        >,
    ) => {
        if (!isAnalyticsAllowed()) {
            return;
        }
        amplitude.track(eventName, {
            ...eventProperties,
            is_impersonating: isImpersonating,
            impersonator_id: impersonatorId,
            is_mobile_app: !!window.isMobileApp,
        });
    };

    /**
     * This function allows us to ensure an event is always attached to a client in amplitude even if the user who emits the event is a coach.
     */
    const trackEventAttachedToClient = (
        currentUser: User,
        clientId: number,
        eventName: string,
        eventProperties?: Record<string, string | number | boolean | object>,
    ) => {
        if (currentUser.type === "client") {
            if (currentUser.id !== clientId) {
                throw new Error(
                    "Client cannot track events on behalf of another client",
                );
            }
            trackEvent(eventName, eventProperties);
            return;
        }
        window.axios.post(
            route("logOnBehalfOf", {
                client: clientId,
            }),
            {
                eventName,
                eventProperties,
            },
        );
    };

    const identifyUser = (user: Client | Coach) => {
        if (!isAnalyticsAllowed() || !amplitudeInitialized) {
            return;
        }
        const identifier = buildIdentifier(user.type, user.id);
        amplitude.setUserId(identifier);
        lastAmplitudeIdentity.current = identifier;
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("user_type", user.type);
        identifyEvent.set("user_email", user.email ?? "");
        identifyEvent.set("user_phone", user.phone_e164 ?? "");
        identifyEvent.set("first_name", user.first_name);
        identifyEvent.set("last_name", user.last_name);
        identifyEvent.set(
            "has_payment_setup",
            (user.type === "client" && user.has_payment_setup) ?? false,
        );
        amplitude.identify(identifyEvent);
    };

    /**
     * Reading the live URL loses the theme the moment the SPA navigates off the
     * landing page, which on the turbo host is every first-party click — so this
     * goes through loadThm(), which falls back to the params captureUtmParams
     * persisted.
     *
     * Two things gate the send. amplitude.init() only runs in the effect below,
     * so identifying before it would hand the property to an uninitialised
     * client. And identify() attaches to whichever user id is current, so the
     * guard is keyed by identity as well as theme: a signup that swaps the
     * anonymous id for a real one has to re-send, or the client that actually
     * converted carries no attribution.
     *
     * The identity falls back to the last id handed to amplitude rather than to
     * "anonymous", because nothing resets amplitude's user id on logout — an
     * inertia POST that leaves this provider mounted. Claiming a fresh anonymous
     * identity there would re-attach the theme to the profile that just logged
     * out. Resetting amplitude's identity on logout is the real fix, but that
     * changes attribution for every event, not just this one.
     */
    const setMarketingUserProperties = () => {
        if (!isAnalyticsAllowed() || !amplitudeInitialized) {
            return;
        }
        const thm = loadThm();
        if (!thm) {
            return;
        }
        const identity = user
            ? buildIdentifier(user.type, user.id)
            : (lastAmplitudeIdentity.current ?? "anonymous");
        const identifiedThm = `${identity}|${thm}`;
        if (lastIdentifiedThm.current === identifiedThm) {
            return;
        }
        lastIdentifiedThm.current = identifiedThm;
        const identifyEvent = new amplitude.Identify();
        identifyEvent.set("thm", thm);
        amplitude.identify(identifyEvent);
    };

    const getExperimentOverrideCookie = (experimentName: string) => {
        return getCookie<string | null>(`e2e_exp_${experimentName}`, null);
    };

    const getE2EForcedDefaultVariant = () => {
        return getCookie<string | null>(E2E_FORCE_DEFAULT_VARIANT_COOKIE, null);
    };

    const getExperimentVariant = (
        experimentName: string,
        fallback: string = "control",
        triggerExposure: boolean = true,
    ) => {
        if (!experimentationInitialized && isAnalyticsAllowed()) {
            return undefined;
        }
        const experimentOverride = getExperimentOverrideCookie(experimentName);
        if (experimentOverride) {
            return experimentOverride;
        }
        const e2eForcedDefault = getE2EForcedDefaultVariant();
        if (e2eForcedDefault) {
            return e2eForcedDefault;
        }
        if (!isAnalyticsAllowed()) {
            return fallback;
        }
        const variantFromAmplitude =
            experimentation.variant(experimentName).value;
        const variant = variantFromAmplitude ?? fallback;
        if (variantFromAmplitude && triggerExposure) {
            experimentation.exposure(experimentName);
        }
        return variant;
    };

    const triggerExposure = (experimentName: string) => {
        if (!experimentationInitialized && isAnalyticsAllowed()) {
            return;
        }
        if (!isAnalyticsAllowed()) {
            return;
        }
        experimentation.exposure(experimentName);
    };

    const triggerExposureOnBackend = async (experimentName: string) => {
        await window.axios.post(route("expose"), {
            experimentName,
        });
    };

    const buildIdentifier = (userType: string, userId: number) => {
        let retVal = userType + "|" + userId;
        if (import.meta.env.VITE_LOCAL_ENVIRONMENT_ID) {
            retVal += "|" + import.meta.env.VITE_LOCAL_ENVIRONMENT_ID;
        }
        return retVal;
    };

    const isAnalyticsAllowed = () => {
        return cookieSettings.analytics;
    };

    const { user } = usePageProps();
    // identifyUser sets the amplitude user id, so it has to run first — the
    // theme identify below attaches to whichever id is current.
    if (user) {
        identifyUser(user);
    }
    setMarketingUserProperties();

    useEffect(() => {
        if (isAmplitudeInitializationBlocked) return;
        (async () => {
            if (!isAnalyticsAllowed()) {
                optOut();
                setAmplitudeInitialized(false);
                setExperimentationInitialized(true);
                return;
            }
            amplitude.setOptOut(false);
            const sessionReplayTracking = sessionReplayPlugin();
            amplitude.add(sessionReplayTracking);
            amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
                autocapture: true,
                deviceId: getCookie("AMP_deviceId", undefined),
            });
            setAmplitudeInitialized(true);
            try {
                await experimentation.start();
            } catch (_e) {
                /**
                 * Experimentation failed to start. This is likely due to users running an ad or tracking blocker.
                 * We'll set experimentation initialization as completed, but users will always get the fallback variant.
                 */
            } finally {
                setExperimentationInitialized(true);
            }
        })();
    }, [isAnalyticsAllowed(), isAmplitudeInitializationBlocked]);

    return (
        <AnalyticsContext.Provider
            value={
                // If analytics are not allowed, we absolutely do not want to pass functions that actually do
                // anything to the children. This is why we return the initialization object.
                !isAnalyticsAllowed()
                    ? {
                          ...initialAnalyticsContext,
                          // We still want to be able to get experiment variants, so this function needs to manually
                          // check if analytics are allowed.
                          trackEventAttachedToClient,
                          getExperimentVariant,
                          experimentationInitialized,
                          setIsAmplitudeInitializationBlocked,
                      }
                    : {
                          trackEvent,
                          trackEventAttachedToClient,
                          getExperimentVariant,
                          experimentationInitialized,
                          triggerExposure,
                          triggerExposureOnBackend,
                          setIsAmplitudeInitializationBlocked,
                      }
            }
        >
            {children}
        </AnalyticsContext.Provider>
    );
}
